import type { Candle } from "../types";
import type { Swings } from "./SwingEngine";
import type { FVG, LiquidityLevel, OrderBlock, StructureEvent } from "./types";

// Fair Value Gap: 3-candle imbalance where candle[i].low > candle[i-2].high (or inverse).
export const FVGEngine = {
  find(candles: Candle[]): FVG[] {
    const gaps: FVG[] = [];
    for (let i = 2; i < candles.length; i++) {
      const a = candles[i - 2], c = candles[i];
      if (c.low > a.high) gaps.push({ index: i, top: c.low, bottom: a.high, dir: "bull" });
      else if (c.high < a.low) gaps.push({ index: i, top: a.low, bottom: c.high, dir: "bear" });
    }
    return gaps;
  },
};

// Order Block: last opposite-colour candle before the impulse that broke structure.
export const OrderBlockEngine = {
  find(candles: Candle[], events: StructureEvent[]): OrderBlock[] {
    const blocks: OrderBlock[] = [];
    for (const ev of events) {
      let idx = ev.from;
      for (let i = ev.index - 1; i >= Math.max(0, ev.index - 10); i--) {
        const c = candles[i];
        const bull = c.close > c.open;
        if ((ev.dir === "bull" && !bull) || (ev.dir === "bear" && bull)) { idx = i; break; }
      }
      const c = candles[idx];
      if (c) blocks.push({ index: idx, top: Math.max(c.open, c.close), bottom: Math.min(c.open, c.close), dir: ev.dir });
    }
    return blocks;
  },
};

// Liquidity: equal highs / equal lows (resting stops) — consecutive swings within a tolerance.
export const LiquidityEngine = {
  find(candles: Candle[], swings: Swings, tolerance: number): LiquidityLevel[] {
    if (candles.length < 5) return [];
    const recent = candles.slice(-200);
    const avgRange = recent.reduce((s, c) => s + (c.high - c.low), 0) / recent.length || 1;
    const tol = avgRange * tolerance;
    const out: LiquidityLevel[] = [];
    const scan = (list: Swings["highs"], kind: "EQH" | "EQL") => {
      for (let i = 1; i < list.length; i++) {
        const a = list[i - 1], b = list[i];
        if (Math.abs(a.price - b.price) > tol) continue;
        const level = kind === "EQH" ? Math.max(a.price, b.price) : Math.min(a.price, b.price);
        let swept = false;
        for (let k = b.index + 1; k < candles.length; k++) {
          if (kind === "EQH" ? candles[k].close > level : candles[k].close < level) { swept = true; break; }
        }
        if (!swept) out.push({ indices: [a.index, b.index], price: level, kind });
      }
    };
    scan(swings.highs, "EQH");
    scan(swings.lows, "EQL");
    return out;
  },
};
